import { Card, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import {
  StopUiState,
  TIMELINE_COLORS,
  TIMELINE_COMPACT_MAX_WIDTH,
} from '../types';

const I18N_BASE = 'scenes.route_report.components.timeline';

const COLOR_BY_STATE: Record<StopUiState, string> = {
  executed: TIMELINE_COLORS.executed,
  not_executed: TIMELINE_COLORS.notExecuted,
  on_going: TIMELINE_COLORS.onGoing,
  pending: TIMELINE_COLORS.pending,
};

/**
 * Anchos y desplazamientos variables por fila. Con todas las barras iguales el
 * bloque se lee como una tabla vacía en vez de como contenido en camino.
 */
const ROWS = [
  { left: 6, width: 52 },
  { left: 18, width: 48 },
  { left: 2, width: 58 },
  { left: 8, width: 62 },
  { left: 4, width: 66 },
];

/**
 * Copia las medidas de `RouteTimeline` para que al llegar los datos la card no
 * cambie de alto y el resto de la página no se corra.
 *
 * El encabezado, el subtítulo y la leyenda van de verdad, no en gris: se conocen
 * antes de pedir nada, y difuminar lo que ya se sabe hace parpadear la pantalla
 * sin motivo. Solo se difuminan los valores.
 */
function RouteTimelineSkeleton() {
  const { t } = useTranslation();
  const compact = useMediaQuery({
    query: `(max-width: ${TIMELINE_COMPACT_MAX_WIDTH}px)`,
  });

  const legend: StopUiState[] = compact
    ? ['executed', 'not_executed', 'on_going', 'pending']
    : ['executed', 'not_executed'];

  return (
    <Card
      className="route-timeline"
      title={
        <div className="route-timeline__head">
          <div>
            <span className="route-timeline__title">
              {t(`${I18N_BASE}.title`)}
            </span>
            <span className="route-timeline__subtitle">
              {compact
                ? t(`${I18N_BASE}.subtitle_compact`)
                : t(`${I18N_BASE}.subtitle`)}
            </span>
          </div>
          <div className="route-timeline__legend">
            {legend.map((state) => (
              <span key={state}>
                <i style={{ background: COLOR_BY_STATE[state] }} />
                {t(`scenes.route_report.stop_states.${state}`)}
              </span>
            ))}
          </div>
        </div>
      }
    >
      <div
        className={`route-timeline__body${
          !compact ? ' route-timeline__body--now' : ''
        }`}
      >
        {/* La banda de la píldora "ahora" (26px) se reserva acá también,
            aunque todavía no se sepa si el reporte va a ser de hoy: si el
            skeleton no la reservara, la card cambiaría de alto apenas
            llegaran los datos y el reporte resultara ser del día en curso. */}
        {!compact && (
          <div className="route-timeline__axis-wrap">
            <div className="route-timeline__grid">
              <div />
              <div className="route-timeline__axis">
                <Skeleton.Input
                  active
                  block
                  style={{ height: 11, minWidth: 0, marginTop: 12 }}
                />
              </div>
              <div />
            </div>
          </div>
        )}

        <div className="route-timeline__scroll">
          {ROWS.map((row, index) => (
            <div className="route-timeline__row" key={index}>
              <div className="route-timeline__grid">
                <div className="route-timeline__id">
                  <Skeleton.Input
                    active
                    style={{ width: 86, height: 19, minWidth: 0 }}
                  />
                  <Skeleton.Input
                    active
                    style={{
                      width: 132,
                      height: 14,
                      minWidth: 0,
                      marginTop: 1,
                      display: 'block',
                    }}
                  />
                </div>

                {!compact && (
                  <div className="route-timeline__track">
                    <Skeleton.Input
                      active
                      style={{
                        position: 'absolute',
                        left: `${row.left}%`,
                        width: `${row.width}%`,
                        height: 4,
                        minWidth: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    />
                  </div>
                )}

                <div className="route-timeline__count">
                  <Skeleton.Input
                    active
                    style={{ width: 50, height: 15, minWidth: 0 }}
                  />
                </div>

                {compact && (
                  <div className="route-timeline__mobile">
                    <Skeleton.Input
                      active
                      block
                      style={{ height: 14, minWidth: 0 }}
                    />
                    <div className="route-timeline__mobile-foot">
                      <Skeleton.Input
                        active
                        style={{ width: 96, height: 13, minWidth: 0 }}
                      />
                      <Skeleton.Input
                        active
                        style={{ width: 34, height: 13, minWidth: 0 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default RouteTimelineSkeleton;
